'use client';

import { Arrow } from '@sprd/sprd-component-kit/v2';
import { ReactNode, useState } from 'react';
import React from 'react';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { AmplienceLinkType } from '@/types/amplience';
import ProductCard, { ProductCardProps } from '@/components/ProductCard/ProductCard';
import ProgressBar from '@/components/ProgressBar/ProgressBar';
import Slider from '@/components/Slider/Slider';
import AmplienceLink from '../Common/AmplienceLink';

type ProductTypeSliderProps = {
  className?: string;
  heading?: string;
  items: ProductCardProps[];
  showAllLink?: AmplienceLinkType;
  variant?: 'white' | 'gray';
  children?: ReactNode;
};

export default function ProductTypeSlider(props: ProductTypeSliderProps) {
  const { heading, items, showAllLink, className, children } = props;
  const t = useTranslations('general');
  const [progress, setProgress] = useState(0);

  return (
    <section className={clsx('gap-2', className)}>
      <div className="mb-4 flex items-center justify-between px-4 sm:px-6 md:mb-6 md:px-8 lg:px-16 xl:px-[90px]">
        <div className="min-w-[100px]">
          {heading && (
            <h2 className="text-xl font-bold uppercase sm:text-2xl md:text-[1.75rem] lg:text-[2rem]">
              {heading}
            </h2>
          )}
        </div>
        {showAllLink && (
          <AmplienceLink
            link={showAllLink}
            className="flex min-w-20 cursor-pointer items-center justify-end text-sm font-semibold text-black sm:justify-center"
          >
            <span>{showAllLink.label ? showAllLink.label : t('showAll')}</span>
            <Arrow />
          </AmplienceLink>
        )}
      </div>
      <Slider
        className={{
          root: '[--slides-to-show:2.15] sm:[--slides-to-show:3.15] md:[--slides-to-show:4.18] lg:[--slides-to-show:4.2] xl:[--slides-to-show:4.15]',
          navPrev: '!hidden bg-black text-white md:!flex lg:size-16',
          navNext: '!hidden bg-black text-white md:!flex lg:size-16',
        }}
        showNavigation
        onProgressChange={setProgress}
      >
        {items.map((item, index) => (
          <ProductCard
            key={index}
            imageBaseUrl={item.imageBaseUrl}
            images={item.images}
            imageSizes="(min-width: 768px) 33vw, (min-width: 1024) 25vw, 50vw"
            link={item.link}
            price={Number(item.price)}
            priceFormat="from"
            title={item.title}
            brand={item.brand}
            labels={item.labels}
            appearances={item.appearances}
            favorite={item.favorite}
            background={item.background}
            productTypeId={item.productTypeId}
            assortment={item.assortment}
          />
        ))}
      </Slider>
      <div className="px-6 md:px-8 lg:px-16 xl:px-22.5">
        <ProgressBar progress={progress} isVisible={items.length > 4} />
      </div>
      {children}
    </section>
  );
}
